import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import * as AppConfigModule from '../../../config-loader';
import { isCapabilityAccessRequest } from '../utils/access-request-url';
import { isFetchProxyRequest } from '../utils/fetch-proxy-request-url';
import { isDappaAuthoringRequest, isDappaErrorReportedInline } from '../utils/dappa-request-url';
import { isProjectRoutineListGet, isRoutineTriggerPost } from '../utils/routine-request-url';
import { DUPLICATE_HANDLED_BY_CALLER } from './duplicate-handled.context';
import { sanitizeReturnUrl } from '../helpers/return-url.helper';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { isDuplicateEntry, serverErrorCode } from '../interfaces/server-error.interface';

/**
 * DELETE to a single job resource (…/job/{id}), not subpaths like …/job/{id}/cancel.
 * Used to avoid global 403 redirect so job detail can surface permission errors inline.
 */
function isRoutineJobDeleteRequest(req: HttpRequest<unknown>): boolean {
  if (req.method !== 'DELETE') return false;
  const path = req.url.split('?')[0];
  return /\/job\/\d+$/.test(path);
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastrService = inject(ToastrService);
  const router = inject(Router);
  const oauthService = inject(OAuthService);
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Skip error handling for OAuth-related requests
      if (error.error instanceof ArrayBuffer) {
        const uint8Array = new Uint8Array(error.error);
        return throwError(() => ({
          ...error,
          error: { message: String.fromCharCode(...uint8Array).replace(/"/g, '') }
        }));
      }

      if (shouldSkipErrorHandling(req.url)) {
        return throwError(() => error);
      }

      // SCRUM-380: GET list routines may return 500 when a project has no routines; no global toast —
      // ExecutionPlanService.getPlans maps this to an empty list. Only 502/503 keep normal error handling.
      if (
        isProjectRoutineListGet(req.url, req.method) &&
        error.status === 500
      ) {
        return throwError(() => error);
      }

      // SCRUM-460: POST routine trigger failures are handled by the calling component
      // (plans-sidebar, plans, plan-history-modal) which surfaces the actual server error.
      // Suppressing the global interceptor toast avoids double toasts for the user.
      // 401 is excluded so handleUnauthorized() still runs; 5xx is excluded so the global
      // "server error" toast is shown for infrastructure-level failures.
      if (
        isRoutineTriggerPost(req.url, req.method) &&
        error.status !== 401 &&
        error.status < 500
      ) {
        return throwError(() => error);
      }

      // EHQ-187: the project Access tab reports grant and scope failures itself,
      // in the panel the admin is working in, so the global toast would be a
      // second and vaguer report of the same failure.
      //
      // Only 4xx is suppressed, and only what the panel can actually explain:
      // 401 still reaches handleUnauthorized so login works, 5xx still gets the
      // global "server error" toast, and a status of 0 — a network or CORS
      // failure — is left global for the same reason 5xx is. It is the most
      // infrastructure-level failure there is, and "Http failure response … 0
      // Unknown Error" is not something a panel banner should be rendering.
      //
      // 403 is deliberately inside the suppressed range. The global handler
      // navigates away on 403, which for an admin whose adminship was revoked
      // mid-session means being ejected from project settings instead of the tab
      // degrading to its read-only view.
      //
      // Unlike the two precedents above this is keyed on URL alone, not method:
      // the tab's *reads* need the same treatment as its writes. A stale
      // `canAdminister` sends a GET to the admin-gated list, and a 403 there is
      // precisely the ejection this exists to prevent.
      if (
        isCapabilityAccessRequest(req.url) &&
        error.status >= 400 &&
        error.status < 500 &&
        error.status !== 401
      ) {
        return throwError(() => error);
      }

      // EHQ-255: a 403 from fetch-proxy is the egress policy refusing the URL a
      // step asked for ("URL blocked by SSRF protection"), not a statement about
      // the user. Falling through would toast Access Denied *and navigate to
      // /projects*, ejecting someone from the project they are working in
      // because a step reached for a blocked host. The step surfaces it itself.
      if (isFetchProxyRequest(req.url, req.method) && error.status === 403) {
        return throwError(() => error);
      }

      // EHQ-339: the Dappa pages and dialogs show validation failures and 404s
      // inline. 401 (login) and 403 (the "lock the project" warning, and the
      // access-denied path) still go through the global handling below.
      if (isDappaAuthoringRequest(req.url) && isDappaErrorReportedInline(error.status)) {
        return throwError(() => error);
      }

      // EHQ-405: the caller treats a duplicate as "it already exists" and goes
      // on, so an error toast would report a failure that did not happen.
      if (req.context.get(DUPLICATE_HANDLED_BY_CALLER) && isDuplicateEntry(error)) {
        return throwError(() => error);
      }

      // Enhanced error handling with specific validation messages
      const errorMessage = getErrorMessage(error, req.url);

      // Handle different error types
      if (error.status === 401) {
        // Unauthorized - redirect to login, unless the 401 is the page's own
        // requests failing after logout() cleared the tokens: a login started
        // then replaces the logout redirect and sends the next user to the page
        // being left (EHQ-540). Resolved lazily — AuthenticationService needs
        // HttpClient, so injecting it eagerly here would be circular.
        if (!injector.get(AuthenticationService).isLoggingOut) {
          handleUnauthorized(oauthService);
        }
      } else if (error.status === 403) {
        // Single job DELETE 403: keep user on job detail; component shows inline message (SCRUM-399).
        if (isRoutineJobDeleteRequest(req)) {
          return throwError(() => error);
        }
        // Forbidden - is it a read-only / locked / not-locked project state error?
        // Branch on the stable code first; fall back to message substrings only
        // for legacy servers that predate the unified envelope (no code).
        const rawErrorMessage = error.error?.message || '';
        const code = serverErrorCode(error);
        const isReadOnlyProjectError =
          code === 'PROJECT_READ_ONLY' ||
          code === 'PROJECT_LOCKED' ||
          (!code &&
            (rawErrorMessage.includes('read only') ||
              rawErrorMessage.includes('locked by another user')));
        const isProjectNotLockedError =
          code === 'PROJECT_NOT_LOCKED' ||
          (!code &&
            (rawErrorMessage.includes('project is not locked') ||
              rawErrorMessage.includes('not locked')));

        if (isReadOnlyProjectError || isProjectNotLockedError) {
          // Don't redirect for read-only project errors or project not locked errors - just show toast
          // These are expected errors that should be handled gracefully
          if (isProjectNotLockedError) {
            // SCRUM-202 FIX: Show clear warning when project is not locked.
            // Worded generically: the lock guards every edit to HEAD (steps,
            // workflows, Dappa APIs), not only steps.
            toastrService.warning(
              'Please lock the project before making changes',
              'Project Not Locked'
            );
          } else {
            toastrService.warning(
              rawErrorMessage || 'This project is in read-only mode',
              'Read-Only Mode'
            );
          }
        } else {
          // For other 403 errors, show access denied and redirect
          toastrService.error('You do not have permission to access this resource', 'Access Denied');
          router.navigate(['/projects']);
        }
      } else if (error.status === 404) {
        // Not found
        toastrService.error('The requested resource was not found', 'Not Found');
      } else if (error.status >= 500) {
        // Server errors
        toastrService.error('A server error occurred. Please try again later.', 'Server Error');
      } else {
        // Other errors (including 400 with specific validation messages)
        toastrService.error(errorMessage, 'Error');
      }

      // Log error details for debugging
      console.error('HTTP Error:', {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        body: error.error,
        message: errorMessage
      });

      return throwError(() => error);
    })
  );
};

/**
 * Get appropriate error message based on error status and context
 */
function getErrorMessage(error: HttpErrorResponse, url: string): string {
  let errorMessage: string;

  switch (error.status) {
    case 400:
      errorMessage = getValidationErrorMessage(error, url);
      break;
    case 403:
      errorMessage = 'You do not have permission to perform this operation';
      break;
    case 404:
      errorMessage = getNotFoundErrorMessage(url);
      break;
    case 500:
      errorMessage = 'Server error occurred. Please try again later';
      break;
    default:
      errorMessage = error.error?.message || 'An unexpected error occurred';
  }

  return errorMessage;
}

/**
 * Get specific validation error messages based on the error content and URL context
 */
function getValidationErrorMessage(error: HttpErrorResponse, url: string): string {
  const errorMessage = error.error?.message || '';
  const code = serverErrorCode(error);

  // Code-first: branch on the stable error code regardless of URL or wording.
  switch (code) {
    case 'ROUTINE_HAS_RUNNING_JOBS':
    case 'JOB_RUNNING':
      return 'Cannot delete routine while jobs are running. Wait for completion or cancel them first';
    case 'NO_PUBLISHED_VERSION':
      return 'This project has no published version. Publish the project first, or configure the routine to use HEAD';
    case 'WORKFLOW_NOT_FOUND_IN_VERSION':
      return 'The workflow referenced by this routine was not found in the target version';
    case 'INVALID_CRONTAB':
      return 'Invalid cron expression provided';
  }

  // Fallback for legacy servers that predate the unified envelope (no code).
  // Dead branches removed (runsAsId/notebook/scheduledTime/arguments and the
  // connection/dbalias field-name matches never matched a live server message);
  // remaining checks mirror the codes above and degrade to the raw message.
  if (url.includes('/routine') || url.includes('/plan') || url.includes('/job')) {
    const lower = errorMessage.toLowerCase();
    if (lower.includes('cannot delete') && lower.includes('running job')) {
      return errorMessage;
    }
    if (errorMessage.includes('running job')) {
      return 'Cannot delete routine while jobs are running. Wait for completion or cancel them first';
    } else if (errorMessage.includes('no published version')) {
      return 'This project has no published version. Publish the project first, or configure the routine to use HEAD';
    } else if (errorMessage.includes('workflow') && errorMessage.includes('not found')) {
      return 'The workflow referenced by this routine was not found in the target version';
    } else if (errorMessage.includes('cron')) {
      return 'Invalid cron expression provided';
    }
  }

  // Generic validation error
  return errorMessage || 'Invalid request parameters';
}

/**
 * Get specific not found error messages based on URL context
 */
function getNotFoundErrorMessage(url: string): string {
  if (url.includes('/connection')) {
    return 'The requested connection was not found';
  } else if (url.includes('/routine') || url.includes('/plan') || url.includes('/job')) {
    return 'The requested routine or job was not found';
  } else if (url.includes('/dbalias')) {
    return 'The requested database alias was not found';
  } else if (url.includes('/project')) {
    return 'The requested project was not found';
  }

  return 'The requested resource was not found';
}

function shouldSkipErrorHandling(url: string): boolean {
  const environment = AppConfigModule.appConfig.env;
  const issuerUrl = environment.auth.zitadel.issuer;

  // Skip OAuth/OIDC related endpoints
  if (url.startsWith(issuerUrl)) {
    return true;
  }

  // Skip well-known endpoints
  if (url.includes('.well-known/openid-configuration')) {
    return true;
  }

  // Skip token endpoints
  if (
    url.includes('/token') ||
    url.includes('/oauth/token') ||
    url.includes('/oauth2/token')
  ) {
    return true;
  }

  // Skip userinfo endpoint
  if (url.includes('/userinfo')) {
    return true;
  }

  // Skip Box proxy endpoints — BoxService owns user-facing messaging for them.
  // The server now returns the unified error envelope here too (SCRUM-801), so
  // this skip is retained NOT because the body shape is non-standard, but because
  // suppressing interceptor toasts avoids rapid DOM updates that can destabilize
  // Safari's WebContent process during batch operations.
  if (url.includes('/box/proxy') || url.includes('/box/download') || url.includes('/box/upload')) {
    return true;
  }

  // Skip step logs endpoint — component handles 404 (no logs stored) gracefully
  if (/\/steps\/\d+\/logs$/.exec(url)) {
    return true;
  }

  return false;
}

function handleUnauthorized(oauthService: OAuthService): void {
  const loc = new URL(window.location.href);
  const params = loc.searchParams;

  // Don't re-enter while we're processing the IdP redirect back to our redirect
  // URI (/login?code=…&state=…) — restarting would discard the state we're about
  // to consume. Scope this narrowly to that callback so unrelated routes that
  // happen to carry code/state query params still trigger login normally.
  if (loc.pathname === '/login' && params.has('code') && params.has('state')) {
    return;
  }

  // Preserve the page the user was trying to reach so login can return them
  // there. A background 401 here is the dominant unauthenticated entry point
  // (it fires before the user reaches the login page). If the AuthGuard already
  // bounced us to /login, the intended target is in its returnUrl query param;
  // otherwise it's the current in-app location (fragment included).
  const target = params.get('returnUrl') ?? loc.pathname + loc.search + loc.hash;

  // Carry the sanitized target through the IdP via the OAuth `state` param so
  // LoginPageComponent.consumeReturnUrl() can restore it after login.
  oauthService.initLoginFlow(sanitizeReturnUrl(target) ?? '/projects');
}
